import { background, extendTheme } from '@chakra-ui/react'

const colors = {
  dandelion: '#FFE165',
  yellow: {
    10: '#FFF6D0', // new color
    20: '#FFEFAC', // new color
    30: '#FFE787', // new color
    40: '#FFE165', // new color
    50: '#FFDA44', // new color
    60: '#E5BF2C', // new color
    70: '#BF9D25', // new color
    80: '#90721C', // new color
    90: '#624914', // new color
    100: '#38230C' // new color

    // Delete old colors after transition to new design is complete.
    // 50: '#FFF7B6', // old. Occurrences replaced with new, similar, yellow.20
    // 100: '#EAD17B', // old. Occurrences replaced with new, similar, yellow.50
    // 150: '#FFE300', // old. Occurrences replaced with new, similar, yellow.40
    // 200: '#E0C600', // old. Occurrences replaced with new, similar, yellow.60
    // 300: '#E4D100', // old. Occurrences replaced with new, similar, yellow.50
    // 400: '#FDB241' // old. Occurrences replaced with new, similar, yellow.60
  },
  alabaster: '#F6F4E8',
  darkchoc: '#171712',
  grayscale: {
    10: '#F6F4E8',
    20: '#ECE9DA',
    30: '#DAD6C3',
    40: '#BAB6A2',
    50: '#8E8A77',
    60: '#5E5B4C',
    70: '#343229',
    80: '#171712',
    90: '#070605',
    100: '#000000'
  },
  magicmint: '#ADEDD6',
  phthalo: '#0B2730',
  medleve: '#395680',
  cyan: {
    10: '#C4F7E1',
    20: '#ADEDD6',
    30: '#95DFCA',
    40: '#7CCDBC',
    50: '#63B7AC',
    60: '#4B9D98',
    70: '#368081',
    80: '#235E64',
    90: '#153F48',
    100: '#0B2730'
  },
  greenfit: {
    30: '#B1F3A7',
    35: '#a4e29b',
    90: '#264021',
    100: '#1B2D18'
  },
  ishafit: {
    20: '#ECE1DA',
    30: '#FFC398',
    35: '#E6A880',
    40: '#B34910',
    50: '#CD733F',
    70: '#792800',
    90: '#4e3425',
    100: '#322118'
  },
  brand: {
    100: '#00BB92',
    200: '#FFE900',
    300: '#8BCEE3'
  },
  blue: {
    10: '#CAE6EF',
    100: '#99CCE0',
    700: '#3183FE'
  },
  purple: {
    500: '#9E00FF'
  },
  green: {
    50: '#DFF3EF',
    100: '#5ECFB8',
    150: '#03B28F',
    200: '#268F7A',
    250: '#124036',
    300: '#082D0F'
  },
  gray: {
    10: '#F8F9FE',
    20: '#F9F9F9',
    30: '#E8E9F1',
    40: '#D4D6DD',
    50: '#F5F2ED',
    70: '#E5E6EC',
    100: '#E5E3DF',
    150: '#D9D7D2',
    200: '#A6A4A1',
    250: '#666563',
    400: '#646464',
    500: '#71727A',
    600: '#292D32'
  },
  error: '#FF563F',
  black: {
    100: '#000'
  },
  white: {
    100: '#FFF',
    600: '#E5E3DF',
    900: '#A6A4A1'
  },
  warning: {
    50: '#FFBF84',
    100: '#FF843F'
  },
  // Cuidei — pharmacy-based video consultation brand (blue/teal).
  // Blue primary CTA + teal accent + light blue-gray surfaces, per the Lovable mock.
  cuidei: {
    10: '#EEF4FF', // page background / subtle surfaces
    20: '#DCE8FF',
    30: '#BBD1FF',
    40: '#8FB2FF',
    50: '#5B8DEF',
    60: '#2F6BED', // primary CTA
    70: '#1F4FD1', // primary CTA hover
    80: '#1A3FA8',
    90: '#162F7A',
    100: '#101F4D', // headings / deep navy text
    teal: '#1FB6A8', // logo + accent gradient start
    tealDark: '#0E9E92',
    ink: '#0F172A', // near-black text
    muted: '#64748B', // secondary text
    cta: '#EA580C', // conversion CTA on landings — the only orange on the page
    ctaHover: '#C2410C'
  }
}

const theme = extendTheme({
  colors,
  fonts: {
    heading: 'var(--font-manrope)',
    body: 'var(--font-manrope)'
  },
  components: {
    Button: {
      baseStyle: {
        rounded: 'full',
        fontWeight: '500',
        shadow: 'base'
      }
    },
    Input: {
      defaultProps: {
        focusBorderColor: 'brand.100'
      }
    },
    Textarea: {
      defaultProps: {
        focusBorderColor: 'brand.100'
      }
    },
    Checkbox: {
      defaultProps: {
        colorScheme: 'green'
      },
      sizes: {
        xl: {
          control: {
            height: '24px',
            width: '24px',
            borderColor: 'grayscale.30',
            borderRadius: '4px',
            border: '1px',
            _focus: {
              boxShadow: '0 0 0 2px #FFE165'
            },
            _checked: {
              bg: 'phthalo',
              borderColor: 'phthalo',
              _hover: {
                bg: '#0b2730 !important',
                borderColor: '#0b2730 !important'
              }
            }
          },
          icon: {
            color: 'white',
            fontSize: '10px'
          }
        }
      }
    },
    Form: {
      variants: {
        floating: {
          container: {
            _focusWithin: {
              label: {
                display: 'none'
              }
            },
            'input:not(:placeholder-shown) + label, textarea:not(:placeholder-shown) ~ label': {
              display: 'none'
            },
            label: {
              top: 0,
              left: 0,
              zIndex: 2,
              position: 'absolute',
              pointerEvents: 'none',
              mx: 6,
              px: 1,
              my: 3,
              transformOrigin: 'left top',
              color: '#B7B08B',
              backgroundColor: '#ECE9DA'
            },
            input: {
              borderRadius: '100px',
              backgroundColor: '#ECE9DA',
              border: 'none'
            },
            select: {
              borderRadius: '100px',
              backgroundColor: '#ECE9DA',
              border: 'none'
            }
          },
          requiredIndicator: {
            color: '#FF9F69'
          }
        }
      }
    },
    Radio: {
      variants: {
        fit: () => ({
          control: {
            _checked: {
              color: '#FF9F69',
              background: '#ECE9DA',
              borderColor: '#ECE9DA',
              _hover: {
                background: '#ECE9DA',
                borderColor: '#ECE9DA'
              }
            },
            background: '#ECE9DA',
            borderColor: '#ECE9DA'
          }
        })
      }
    },
    Progress: {
      variants: {
        fit: () => ({
          filledTrack: {
            bg: '#3B6534'
          },
          track: {
            bg: '#264021'
          }
        })
      }
    }
  },
  shadows: {
    outline: '0 0 0 3px #FFE165',
    blue: '0 0 0 3px rgba(156, 193, 231, 0.6)',
    outlineSm: '0 0 0 2px rgba(38, 143, 122, 0.6)'
  },
  styles: {
    global: {
      '*': {
        scrollbarWidth: 'thin',
        scrollbarColor: '#E0C600 #D9D7D2',
        '&::-webkit-scrollbar': {
          width: '6px',
          height: '6px'
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'yellow.60'
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'gray.150'
        }
      },
      // Cuidei brand: blue scrollbar instead of the Omens yellow. Scoped via the
      // data-brand attribute set on <html> for Cuidei pages (see _app.tsx).
      'html[data-brand="cuidei"], html[data-brand="cuidei"] *': {
        scrollbarColor: '#2F6BED #DCE8FF',
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'cuidei.60'
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'cuidei.10'
        }
      },
      '*::-moz-selection': {
        backgroundColor: 'green.250',
        color: '#FFF'
      },
      '*::selection': {
        backgroundColor: 'green.250',
        color: '#FFF'
      },
      body: {
        color: 'phthalo'
      },
      a: {
        textDecoration: 'none !important',
        cursor: 'pointer'
      },
      select: {
        bg: 'white'
      },
      option: {
        bg: 'white !important',
        fontFamily: 'var(--font-manrope)',
        lineHeight: 'var(--chakra-lineHeights-normal)',
        fontSize: 'var(--chakra-fontSizes-md)',
        fontWeight: '600'
      },
      // SurveyJs
      '.sv-root-modern': {
        h: 'full',
        form: {
          h: 'full'
        },
        ul: {
          pl: 4
        },
        '.sv-container-modern': {
          h: 'full',
          mb: '0 !important'
        },
        '.sv-progress__text': {
          display: 'none'
        },
        '.sv-progress': {
          bg: 'gray.150',
          pos: 'relative',
          m: 'var(--chakra-space-6) 0',
          borderRadius: '20px'
        },
        '.sv-progress__bar': {
          bg: 'yellow.60',
          height: '.2em',
          borderRadius: '20px',
          transition: 'width 250ms ease-out',
          '& span': {
            display: 'none'
          }
        },
        '.sv-page': {
          m: 0
        },
        '.sv-title': {
          color: 'black',
          fontWeight: '700',
          fontSize: 'lg',
          mb: '4',
          rounded: 'xl'
        },
        '.sv-question__title--answer': {
          bg: 'transparent !important'
        },
        '.sv-question__title--error': {
          bg: '#ffb0a5' // polished lighten(0.2, colors.error), precomputed: polished cost 36 KB on every page
        },
        '.sv-radio, .sv-checkbox': {
          label: {
            cursor: 'pointer',
            rounded: '8px',
            my: '2',
            mx: 0,
            p: '3',
            pt: '1',
            pos: 'relative',
            display: 'flex',
            w: 'fit-content',
            bg: 'gray.50',
            shadow: 'md',
            transitionProperty: 'color, background-color',
            transitionDuration: '250ms',
            transitionTimingFunction: 'ease-out',
            '&:hover': {
              bg: 'yellow.20'
            }
          },
          '&.sv-radio--checked, &.sv-checkbox--checked': {
            label: {
              bg: 'yellow.20'
            }
          },
          '.sv-item__decorator:not(td .sv-item__decorator)': {
            display: 'none',
            w: 0
          }
        },
        'textarea.sv-comment': {
          rounded: 'lg',
          p: 4,
          resize: 'none !important',
          '&:focus, &:active': {
            boxShadow: 'var(--chakra-shadows-outlineSm)'
          }
        },
        '.sv-action-bar': {
          m: '0 !important'
        },
        '.sv-btn': {
          bg: 'yellow.400',
          color: 'black',
          shadow: 'base',
          w: 'fit-content',
          fontWeight: '500',
          paddingInlineStart: '4',
          paddingInlineEnd: '4',
          transitionProperty: 'var(--chakra-transition-property-common)',
          transitionDuration: 'var(--chakra-transition-duration-normal)',
          '&:hover': {
            bg: 'yellow.500'
          }
        },
        '.sv-question__content': {
          p: 0
        },
        '*': {
          fontFamily: 'var(--font-manrope) !important'
        }
      },
      // PhoneInput
      '.PhoneInputInput': {
        width: '100%',
        height: 'var(--input-height)',
        fontSize: 'var(--input-font-size)',
        paddingInlineStart: 'var(--input-padding)',
        paddingInlineEnd: 'var(--input-padding)',
        borderRadius: 'var(--input-border-radius)',
        minWidth: '0px',
        outline: '2px solid transparent',
        outlineOffset: '2px',
        position: 'relative',
        appearance: 'none',
        transitionProperty: 'var(--chakra-transition-property-common)',
        transitionDuration: 'var(--chakra-transition-duration-normal)',
        '--input-font-size': 'var(--chakra-fontSizes-md)',
        '--input-padding': 'var(--chakra-space-4)',
        '--input-border-radius': 'var(--chakra-radii-md)',
        '--input-height': 'var(--chakra-sizes-10)',
        border: '1px solid',
        borderTopColor: 'currentcolor',
        borderRightColor: 'currentcolor',
        borderBottomColor: 'currentcolor',
        borderLeftColor: 'currentcolor',
        borderColor: 'inherit',
        background: 'inherit',
        '&:hover': {
          borderColor: 'var(--chakra-colors-gray-300)'
        },
        '&:active, &:focus, &:focus-visible': {
          zIndex: 1,
          borderColor: 'var(--chakra-colors-brand-100)',
          boxShadow: '0 0 0 1px var(--chakra-colors-brand-100)'
        },
        '&[data-invalid],&[aria-invalid="true"]': {
          borderColor: '#E53E3E',
          boxShadow: '0 0 0 1px #E53E3E'
        }
      },
      '.CustomPhoneInputWeight': {
        '& > input': {
          border: 'none',
          padding: '24px',
          background: 'grayscale.20',
          borderRadius: '100px',

          '&::placeholder': {
            color: '#B7B08B'
          }
        }
      }
    }
  },
  config: {
    initialColorMode: 'light',
    useSystemColorMode: false
  },
  textStyles: {
    super: {
      // color: 'darkchoc',
      fontWeight: '800',
      fontSize: '6.5rem',
      fontFeatureSettings: "'ss01' on",
      letterSpacing: '-0.03em',
      lineHeight: '7rem'
    },
    superSm: {
      // color: 'darkchoc',
      fontWeight: '800',
      fontSize: '4.25rem',
      fontFeatureSettings: "'ss01' on",
      letterSpacing: '-0.04em',
      lineHeight: '4.5rem'
    },
    h1: {
      // color: 'darkchoc',
      fontWeight: '800',
      fontSize: '3.25rem',
      fontFeatureSettings: "'ss01' on, 'liga' off, 'calt' off",
      letterSpacing: '-0.04em',
      lineHeight: '3.75rem'
    },
    h1Sm: {
      // color: 'darkchoc',
      fontWeigh: '800',
      fontSize: '2rem',
      lineHeight: '2.5rem'
    },
    h2: {
      // color: 'darkchoc',
      fontWeight: '800',
      fontSize: '2.75rem',
      fontFeatureSettings: "'ss01' on, 'liga' off, 'calt' off",
      letterSpacing: '-0.03em',
      lineHeight: '3.25rem'
    },
    h2sm: {
      // color: 'darkchoc',
      fontWeight: '800',
      fontSize: '1.75rem',
      lineHeight: '2rem'
    },
    h3: {
      // color: 'darkchoc',
      fontWeight: '800',
      fontSize: '2.5rem',
      fontFeatureSettings: "'ss01' on, 'calt' off, 'liga' off",
      letterSpacing: '-0.03em',
      lineHeight: '3rem'
    },
    h3sm: {
      // color: 'darkchoc',
      fontWeight: '800',
      fontSize: '1.5rem',
      fontFeatureSettings: "'ss01' on, 'calt' off, 'liga' off",
      letterSpacing: '-0.03em',
      lineHeight: '1.75rem'
    },
    h4: {
      // color: 'darkchoc',
      fontWeight: '800',
      fontSize: '2.25rem',
      fontFeatureSettings: "'ss07' on, 'ss01' on, 'calt' off, 'liga' off",
      letterSpacing: '-0.03em',
      lineHeight: '2.5rem'
    },
    h4sm: {
      // color: 'darkchoc',
      fontWeight: '800',
      fontSize: '1.25rem',
      fontFeatureSettings: "'ss07' on, 'ss01' on, 'calt' off, 'liga' off",
      letterSpacing: '-0.03em',
      lineHeight: '1.5rem'
    },
    h5: {
      // color: 'darkchoc',
      fontWeight: '800',
      fontSize: '1.5rem',
      fontFeatureSettings: "'ss01' on, 'liga' off, 'calt' off",
      letterSpacing: '-0.03em',
      lineHeight: '1.75rem'
    },
    h5sm: {
      // color: 'darkchoc',
      fontWeight: '800',
      fontSize: '17px',
      fontFeatureSettings: "'ss01' on, 'liga' off, 'calt' off",
      letterSpacing: '-0.01em',
      lineHeight: '20px'
    },
    body1: {
      // color: 'darkchoc',
      fontWeight: '600',
      fontSize: '17px',
      letterSpacing: '-0.02em',
      lineHeight: '24px'
    },
    body1sm: {
      // color: 'darkchoc',
      fontWeight: '600',
      fontSize: '1rem',
      letterSpacing: '-0.02em',
      lineHeight: '1.25rem'
    },
    body2: {
      // color: 'darkchoc',
      fontWeight: '600',

      fontSize: '15px',
      letterSpacing: '-0.01em',
      lineHeight: '20px'
    },
    body2strong: {
      // color: 'darkchoc',
      fontWeight: '700',

      fontSize: '15px',
      letterSpacing: '-0.01em',
      lineHeight: '20px'
    },
    body2sm: {
      // color: 'darkchoc',
      fontWeight: '600',
      fontSize: '14px',
      letterSpacing: '-0.01em',
      lineHeight: '18px'
    },
    body3: {
      // color: 'darkchoc',
      fontWeight: '600',
      fontSize: '13px',
      letterSpacing: '-0.02em',
      lineHeight: '18px'
    },
    body3sm: {
      // color: 'darkchoc',
      fontWeight: '600',
      fontSize: '12px',
      lineHeight: '16px'
    },
    label: {
      // color: 'darkchoc',
      fontWeight: '700',
      fontSize: '15px',
      fontFeatureSettings: "'ss01' on, 'calt' off, 'liga' off",
      letterSpacing: '-0.01em',
      lineHeight: '20px'
    },
    labelsm: {
      // color: 'darkchoc',
      fontWeight: '600',
      fontSize: '14px',
      fontFeatureSettings: "'ss01' on, 'calt' off, 'liga' off",
      letterSpacing: '-0.01em',
      lineHeight: '18px'
    }
  }
})

export type Theme = typeof theme

export default theme
